import { Component, OnInit, inject } from '@angular/core';
import { BadgeStatus, ScorecardFacade, ScorecardV2Badge, ScorecardV2Query } from '@fgb/core';
import { combineLatest, Observable } from 'rxjs';
import { map, shareReplay } from 'rxjs/operators';
import { ScorecardsV2DescriptionService } from '../scorecards-v2-description.service';

/** A badge with the lock state of the scorecard it belongs to. */
export interface ScorecardsV2Badge extends ScorecardV2Badge {
  /** True while the scorecard is locked by the API, so its parent scorecard must be completed first */
  Locked: boolean;
}

/** A badge category with its badges, completed ones first. */
export interface ScorecardsV2BadgeCategory<T extends ScorecardV2Badge = ScorecardsV2Badge> {
  /** Name of the category, as given in the badge's Categories */
  Title: string;
  Badges: T[];
}

/**
 * Groups the badges by category. A badge with several categories is added to each, and one with none is skipped.
 * The v2 response has no category ordinal yet, so categories follow the first appearance of their first badge
 * until the API adds one. Within a category completed badges come first, then the rest, each in API order.
 */
function groupBadgesByCategory(badges: ScorecardV2Badge[]): ScorecardsV2BadgeCategory<ScorecardV2Badge>[] {
  const groups = new Map<string, ScorecardV2Badge[]>();
  for (const badge of badges) {
    for (const title of badge.Categories ?? []) {
      const list = groups.get(title);
      list ? list.push(badge) : groups.set(title, [badge]);
    }
  }
  return [...groups].map(([Title, list]) => ({
    Title,
    Badges: [
      ...list.filter((b) => b.Status === BadgeStatus.Completed),
      ...list.filter((b) => b.Status === BadgeStatus.NotCompleted),
    ],
  }));
}

@Component({
  selector: 'fgbcl-scorecards-v2-badges',
  templateUrl: './scorecards-v2-badges.component.html',
  styleUrl: './scorecards-v2-badges.component.scss',
})
export class ScorecardsV2BadgesComponent implements OnInit {
  private scorecardFacade = inject(ScorecardFacade);
  private scorecardQuery = inject(ScorecardV2Query);
  private descriptionService = inject(ScorecardsV2DescriptionService);

  /** True once the scorecards (and their badges) have loaded, so the empty state does not flash while loading */
  readonly isLoaded = this.scorecardQuery.isLoaded;

  badgesByCategory$: Observable<ScorecardsV2BadgeCategory[]>;
  completed$: Observable<number>;
  inProgress$: Observable<number>;
  locked$: Observable<number>;
  readonly BadgeStatus = BadgeStatus;
  readonly INITIAL_VISIBLE = 6;
  expandedCategories = new Set<number>();
  loadMoreStartIndices = new Map<number, number>();

  ngOnInit(): void {
    this.scorecardFacade.fetchScorecards();

    // Badges have no lock state, so it is taken from the scorecard each badge belongs to
    this.badgesByCategory$ = combineLatest([this.scorecardQuery.selectBadges(), this.scorecardQuery.selectScorecards()]).pipe(
      map(([badges, scorecards]) => {
        const lockedIds = new Set(scorecards.filter((s) => s.Locked).map((s) => s.ScorecardId));
        return groupBadgesByCategory(badges).map((category) => ({
          ...category,
          Badges: category.Badges.map((badge) => ({
            ...badge,
            Locked: badge.Status !== BadgeStatus.Completed && lockedIds.has(badge.ScorecardId),
          })),
        }));
      }),
      shareReplay({ bufferSize: 1, refCount: true })
    );

    const allBadges$ = this.badgesByCategory$.pipe(map((categories) => categories.flatMap((c) => c.Badges)));

    this.completed$ = allBadges$.pipe(map((badges) => badges.filter((b) => b.Status === BadgeStatus.Completed).length));
    this.inProgress$ = allBadges$.pipe(
      map((badges) => badges.filter((b) => b.Status === BadgeStatus.NotCompleted && !b.Locked).length)
    );
    this.locked$ = allBadges$.pipe(map((badges) => badges.filter((b) => b.Locked).length));
  }

  getVisibleBadges(index: number, badges: ScorecardsV2Badge[]): ScorecardsV2Badge[] {
    if (this.expandedCategories.has(index)) {
      return badges;
    }
    return badges.slice(0, this.INITIAL_VISIBLE);
  }

  loadMore(index: number): void {
    this.expandedCategories.add(index);
    this.loadMoreStartIndices.set(index, this.INITIAL_VISIBLE);
  }

  /** Opens the description panel for the scorecard the badge belongs to, locked ones included */
  openScorecard(badge: ScorecardsV2Badge): void {
    if (!badge?.ScorecardId) return;
    this.descriptionService.open(badge.ScorecardId);
  }

  getBadgeAnimClass(categoryIndex: number, badgeIndex: number): string {
    const startIndex = this.loadMoreStartIndices.get(categoryIndex) ?? 0;
    if (badgeIndex < startIndex) return '';
    const delay = Math.min(badgeIndex - startIndex + 1, 6);
    return `animate-in delay-${delay}`;
  }
}
